import {
	Divider,
	designTokens,
	Flex,
	Heading,
	InputSelect,
	Label,
	Loader,
	YokuaruBody,
	YokuaruHeader
} from "@itandi/itandi-bb-ui";
import dayjs from "dayjs";
import { useCallback, useState } from "react";
import { DefaultV2 } from "~/components/common/layouts/DefaultV2";
import { useHomes } from "~/hooks/useHomes";
import { unassignedStaff } from "~/models/CompanyDealTodo";
import type { CompanyStoreSetting } from "~/models/CompanyStoreSetting";
import type { DealType } from "~/models/Deal";
import {
	dealApplicationStatusQueryParam,
	dealStatusQueryParam,
	dealTypeQueryParam,
	pendingAcceptanceQueryParam,
	pendingEstimateAcceptQueryParam,
	pendingOrderQueryParam,
	todoQueryParam
} from "~/models/DealDashboard";
import type { ItandiAccount } from "~/models/ItandiAccount";
import { formatHyphenDate } from "~/utils/DateUtil";
import { DealTypeFilter } from "../common/home/DealTypeFilter";
import type { StatusCardItems } from "../common/home/StatusCardList";
import { StatusCardList } from "../common/home/StatusCardList";

type Props = {
	companyStoreSettings: CompanyStoreSetting[];
	currentStore: Exclude<ItandiAccount["store"], null>;
};

export function Page({ currentStore, companyStoreSettings }: Props) {
	const today = dayjs();
	const formatToday = formatHyphenDate(today.toDate())!;
	const twoDaysLater = formatHyphenDate(today.add(2, "day").toDate())!;
	const [authId, setAuthId] = useState(currentStore.authId);
	const [dealTypes, setDealTypes] = useState<DealType[]>([
		"restoration",
		"repair",
		"other"
	]);
	const { masterAccount } = currentStore;
	const handleStoreChange = useCallback((storeAuthId: number) => {
		setAuthId(storeAuthId);
	}, []);

	const { data: dealDashboard } = useHomes({ authId, dealTypes });
	if (dealDashboard === null) return <Loader />;

	const dealTypeCards = [
		{
			count: dealDashboard.dealTypeInfo.restorationCount,
			href: `/deals?${dealTypeQueryParam(
				"restoration",
				authId,
				masterAccount
			)}`,
			label: "原状回復",
			variant: "default"
		},
		{
			count: dealDashboard.dealTypeInfo.repairCount,
			href: `/deals?${dealTypeQueryParam("repair", authId, masterAccount)}`,
			label: "修繕",
			variant: "default"
		},
		{
			count: dealDashboard.dealTypeInfo.otherCount,
			href: `/deals?${dealTypeQueryParam("other", authId, masterAccount)}`,
			label: "その他",
			variant: "default"
		}
	] as const satisfies StatusCardItems;

	const dealStatusCards = [
		{
			count: dealDashboard.dealStatusInfo.nowAcceptingCount,
			href: `/deals?${dealStatusQueryParam(
				["10"],
				dealTypes,
				authId,
				masterAccount
			)}`,
			label: "業者選択中",
			variant: "default"
		},
		{
			count: dealDashboard.dealStatusInfo.inEstimationCount,
			href: `/deals?${dealStatusQueryParam(
				["15", "101"],
				dealTypes,
				authId,
				masterAccount
			)}`,
			label: "見積作成中",
			variant: "default"
		},
		{
			count: dealDashboard.dealStatusInfo.sendEstimateCount,
			href: `/deals?${dealStatusQueryParam(
				["40", "105"],
				dealTypes,
				authId,
				masterAccount
			)}`,
			label: "見積確認中",
			variant: "default"
		},
		{
			count: dealDashboard.dealStatusInfo.inConstructionCount,
			href: `/deals?${dealStatusQueryParam(
				["45", "111"],
				dealTypes,
				authId,
				masterAccount
			)}`,
			label: "工事完了待ち",
			variant: "default"
		},
		{
			count: dealDashboard.dealStatusInfo.awaitingAcceptanceCount,
			href: `/deals?${dealStatusQueryParam(
				["90", "190"],
				dealTypes,
				authId,
				masterAccount
			)}`,
			label: "検収中",
			variant: "default"
		},
		{
			count: dealDashboard.dealStatusInfo.finishedCount,
			href: `/deals?${dealStatusQueryParam(
				["95", "100", "200"],
				dealTypes,
				authId,
				masterAccount
			)}`,
			label: "完了",
			variant: "default"
		}
	] as const satisfies StatusCardItems;

	const dealApplicationStatusCards = [
		{
			count: dealDashboard.dealApplicationTypeInfo.requestedCount,
			href: `/deals?${dealApplicationStatusQueryParam(
				"requestEstimation",
				dealTypes,
				authId,
				masterAccount
			)}`,
			label: "見積申請あり",
			variant: "default"
		},
		{
			count: dealDashboard.dealApplicationTypeInfo.requestCount,
			href: `/deals?${dealApplicationStatusQueryParam(
				"requestEstimationByCompany",
				dealTypes,
				authId,
				masterAccount
			)}`,
			label: "見積申請中",
			variant: "default"
		},
		{
			count: dealDashboard.dealApplicationTypeInfo.draftCount,
			href: `/deals?${dealApplicationStatusQueryParam(
				"draftEstimationByCompany",
				dealTypes,
				authId,
				masterAccount
			)}`,
			label: "見積編集中",
			variant: "default"
		},
		{
			count: dealDashboard.dealApplicationTypeInfo.waitingCancelCount,
			href: `/deals?${dealApplicationStatusQueryParam(
				"cancel",
				dealTypes,
				authId,
				masterAccount
			)}`,
			label: "取下申請",
			variant: "default"
		},
		{
			count: dealDashboard.dealApplicationTypeInfo.acceptedCancelCount,
			href: `/deals?${dealStatusQueryParam(
				["901"],
				dealTypes,
				authId,
				masterAccount
			)}`,
			label: "取下完了",
			variant: "default"
		}
	] as const satisfies StatusCardItems;

	const dealTodoCards = [
		{
			count: dealDashboard.dealTodoInfo.dueOverCount,
			href: `/todos?${todoQueryParam(
				{ completed: false, dueDateTo: formatToday },
				authId,
				masterAccount
			)}`,
			label: "期限切れ",
			variant:
				dealDashboard.dealTodoInfo.dueOverCount === 0 ? "success" : "danger"
		},
		{
			count: dealDashboard.dealTodoInfo.dueTodayCount,
			href: `/todos?${todoQueryParam(
				{ completed: false, dueDateFrom: formatToday, dueDateTo: formatToday },
				authId,
				masterAccount
			)}`,
			label: "本日中期限",
			variant:
				dealDashboard.dealTodoInfo.dueTodayCount === 0 ? "success" : "warning"
		},
		{
			count: dealDashboard.dealTodoInfo.dueInThreeDaysCount,
			href: `/todos?${todoQueryParam(
				{ completed: false, dueDateFrom: formatToday, dueDateTo: twoDaysLater },
				authId,
				masterAccount
			)}`,
			label: "期限3日以内",
			variant: "default"
		},
		{
			count: dealDashboard.dealTodoInfo.incompleteCount,
			href: `/todos?${todoQueryParam(
				{ completed: false },
				authId,
				masterAccount
			)}`,
			label: "未完了",
			variant: "default"
		},
		{
			count: dealDashboard.dealTodoInfo.unassignedCount,
			href: `/todos?${todoQueryParam(
				{ completed: false, staffId: unassignedStaff.value },
				authId,
				masterAccount
			)}`,
			label: "担当者未設定",
			variant: "default"
		}
	] as const satisfies StatusCardItems;

	const dealRequiredActionCards = [
		{
			count: dealDashboard.dealRequiredActionInfo.pendingOrderCount,
			description: "見積提出から\n1週間経過",
			href: `/deals?${pendingOrderQueryParam(
				formatHyphenDate(today.subtract(7, "day").toDate())!,
				authId,
				masterAccount
			)}`,
			label: "長期未発注",
			variant:
				dealDashboard.dealRequiredActionInfo.pendingOrderCount > 0
					? "danger"
					: "success"
		},
		{
			count: dealDashboard.dealRequiredActionInfo.pendingEstimateAcceptCount,
			description: "見積申請から\n3日経過",
			href: `/deals?${pendingEstimateAcceptQueryParam(
				formatHyphenDate(today.subtract(3, "day").toDate())!,
				authId,
				masterAccount
			)}`,
			label: "見積承認待ち",
			variant:
				dealDashboard.dealRequiredActionInfo.pendingEstimateAcceptCount > 0
					? "warning"
					: "success"
		},
		{
			count: dealDashboard.dealRequiredActionInfo.pendingAcceptanceCount,
			description: "検収依頼から\n1週間経過",
			href: `/deals?${pendingAcceptanceQueryParam(
				formatHyphenDate(today.subtract(7, "day").toDate())!,
				authId,
				masterAccount
			)}`,
			label: "検収未完了",
			variant:
				dealDashboard.dealRequiredActionInfo.pendingAcceptanceCount > 0
					? "warning"
					: "success"
		}
	] as const satisfies StatusCardItems;

	return (
		<DefaultV2>
			<YokuaruHeader
				heading={<Heading as="h1">ダッシュボード</Heading>}
				rightArea={
					masterAccount ? (
						<Flex alignItems="center" gap={designTokens["spacing/xxs"]}>
							<Label color="subtle" size="s">
								表示店舗
							</Label>
							<InputSelect
								onChange={(e) => {
									handleStoreChange(Number(e.target.value));
								}}
								value={authId}
							>
								{companyStoreSettings.map((store) => (
									<option key={store.authId} value={store.authId}>
										{store.name}
									</option>
								))}
							</InputSelect>
						</Flex>
					) : undefined
				}
			/>
			<YokuaruBody>
				<Flex direction="column" gap={designTokens["spacing/m"]} width="100%">
					<Heading as="h2">要対応のアクション</Heading>
					<StatusCardList statusCards={dealRequiredActionCards} />
				</Flex>
				<Flex direction="column" gap={designTokens["spacing/m"]} width="100%">
					<Heading as="h2">進行中の案件</Heading>
					<StatusCardList statusCards={dealTypeCards} />
				</Flex>
				<Flex direction="column" gap={designTokens["spacing/m"]} width="100%">
					<DealTypeFilter
						dealDashboard={dealDashboard}
						dealTypes={dealTypes}
						setDealTypes={setDealTypes}
					/>
					<Flex
						direction="column"
						gap={designTokens["spacing/xs"]}
						width="100%"
					>
						<Heading as="h3" color="subtle">
							ステータス状況
						</Heading>
						<StatusCardList statusCards={dealStatusCards} />
					</Flex>
					<Flex
						direction="column"
						gap={designTokens["spacing/xs"]}
						width="100%"
					>
						<Heading as="h3" color="subtle">
							申請状況
						</Heading>
						<StatusCardList statusCards={dealApplicationStatusCards} />
					</Flex>
				</Flex>
				<Divider color={designTokens["color/border/default"]} />
				<Flex direction="column" gap={designTokens["spacing/m"]} width="100%">
					<Heading as="h2">TODO状況</Heading>
					<StatusCardList statusCards={dealTodoCards} />
				</Flex>
			</YokuaruBody>
		</DefaultV2>
	);
}
